'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { cn } from './cn';
import { AlertCircle, X } from 'lucide-react';
import { Button } from './Button';
import { isTopDialog, pushDialog, removeDialog } from './dialogStack';
import { focusableIn, restoreFocus } from './focus';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /**
   * When false, hides the header close (X) button and stops backdrop clicks
   * and Escape from closing the modal, for steps where an accidental dismiss
   * would silently and irrecoverably discard something the caller can't get
   * back (e.g. a one-time link). Defaults to true (existing behavior unchanged).
   */
  dismissable?: boolean;
  /** Accessible name of the header close button. Defaults to Indonesian, like ConfirmModal's texts. */
  closeLabel?: string;
  /** 'alertdialog' for interrupting confirmations (ConfirmModal sets it). Defaults to 'dialog'. */
  role?: 'dialog' | 'alertdialog';
  /** Id of an element in the body that describes the dialog; defaults to the description, if any. */
  describedById?: string;
}

const sizeStyles = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
};

export function Modal({ isOpen, ...rest }: ModalProps) {
  // The dialog lives in its own component so every open starts from fresh
  // state: the opener is read before anything inside can autoFocus.
  return isOpen ? <ModalDialog {...rest} /> : null;
}

function ModalDialog({
  onClose,
  title,
  description,
  children,
  size = 'md',
  dismissable = true,
  closeLabel = 'Tutup',
  role = 'dialog',
  describedById,
}: Omit<ModalProps, 'isOpen'>) {
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descId = `${baseId}-desc`;
  const dialogRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [opener] = useState<HTMLElement | null>(() =>
    typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );

  // Latest props for the document listener, without re-running the mount effect.
  const latest = useRef({ onClose, dismissable });
  useEffect(() => {
    latest.current = { onClose, dismissable };
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    const entry = { container: dialog };
    pushDialog(entry);

    // Something inside already took focus (autoFocus): leave it. Otherwise the
    // body first (the form field, or Cancel in a confirmation) so the close
    // button is not the landing spot; the dialog itself when nothing fits.
    if (dialog && !dialog.contains(document.activeElement)) {
      const initial = focusableIn(bodyRef.current)[0] ?? focusableIn(dialog)[0] ?? dialog;
      initial.focus();
    }

    function onKeyDown(e: KeyboardEvent) {
      if (!isTopDialog(entry)) return;
      if (e.key === 'Escape') {
        // An inner control (combobox, dropdown) that handled Escape wins.
        if (e.defaultPrevented) return;
        if (latest.current.dismissable) {
          e.preventDefault();
          latest.current.onClose();
        }
        return;
      }
      if (e.key !== 'Tab' || !dialog) return;
      const items = focusableIn(dialog);
      if (items.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      // Focus outside, or on the shell after a step swap removed the focused
      // control: pull it back in.
      if (!active || !dialog.contains(active) || active === dialog) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      removeDialog(entry);
      restoreFocus(opener);
    };
  }, [opener]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      onClick={dismissable ? onClose : undefined}
    >
      <div
        ref={dialogRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedById ?? (description ? descId : undefined)}
        tabIndex={-1}
        className={cn(
          'bg-white rounded-xl w-full shadow-xl outline-none',
          sizeStyles[size]
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 id={titleId} className="text-base font-semibold text-slate-900">{title}</h2>
            {description && <p id={descId} className="text-sm text-slate-500 mt-0.5">{description}</p>}
          </div>
          {dismissable && (
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </div>
        <div ref={bodyRef} className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
}

export interface ConfirmModalProps extends Omit<ModalProps, 'children' | 'role' | 'describedById'> {
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  variant?: 'danger' | 'warning' | 'success';
  subtitle?: string;
}

export function ConfirmModal({
  isOpen,
  onClose,
  title,
  description,
  subtitle,
  confirmText = 'Konfirmasi',
  cancelText = 'Batal',
  onConfirm,
  onCancel,
  isLoading = false,
  variant = 'danger',
  dismissable = true,
  closeLabel,
}: ConfirmModalProps) {
  const bodyId = `${useId()}-body`;
  const iconColors = {
    danger: 'bg-red-100 text-red-600',
    warning: 'bg-amber-100 text-amber-600',
    success: 'bg-emerald-100 text-emerald-600',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      dismissable={dismissable}
      closeLabel={closeLabel}
      role="alertdialog"
      describedById={description ? bodyId : undefined}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={cn('w-10 h-10 rounded-full flex items-center justify-center shrink-0', iconColors[variant])}>
          <AlertCircle className="w-5 h-5" aria-hidden="true" />
        </div>
        <p className="text-xs text-slate-500">{subtitle || 'Tindakan ini tidak dapat dibatalkan.'}</p>
      </div>

      {description && (
        <p id={bodyId} className="text-sm text-slate-600 mb-6 whitespace-pre-line">{description}</p>
      )}

      <div className="flex gap-3">
        <Button
          variant="secondary"
          className="flex-1"
          onClick={onCancel}
          disabled={isLoading}
        >
          {cancelText}
        </Button>
        <Button
          variant={variant === 'danger' ? 'danger' : 'primary'}
          className="flex-1"
          onClick={onConfirm}
          isLoading={isLoading}
        >
          {confirmText}
        </Button>
      </div>
    </Modal>
  );
}
